import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import MiseLogo from "./MiseLogo";

const linkBase =
  "text-sm tracking-wide text-white/70 hover:text-white transition-colors";
const linkActive = "!text-white";

export const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const hidePilotChip =
    pathname.startsWith("/family-meals/join") ||
    pathname.startsWith("/heart-of-house/pilot") ||
    pathname.startsWith("/admin");

  // Contextual CTA — the label and destination change to match the page's
  // most relevant action.
  const getPilotCta = () => {
    if (pathname.startsWith("/at-home")) {
      // the app is live: "Get the app" goes straight to the Google Play listing
      return { to: "https://play.google.com/store/apps/details?id=mise.at.home", label: t.nav.pilotCtaAtHome };
    }
    if (pathname.startsWith("/family-meals")) {
      // the network is live: sign-up happens on misefamilymeals.ca
      return { to: "https://misefamilymeals.ca/register", label: t.nav.pilotCtaFamilyMeals };
    }
    // Every "Pilot interest" button goes to the founding pilot program: Heart of House, About, Home, Contact
    // and anything else
    if (pathname.startsWith("/heart-of-house") || pathname.startsWith("/about")) {
      return { to: "/heart-of-house/pilot", label: t.nav.pilotCtaHoh };
    }
    return { to: "/heart-of-house/pilot", label: t.nav.pilotCta };
  };
  const pilotCta = getPilotCta();

  const navItems = [
    { to: "/", label: t.nav.home, end: true, id: "nav-home" },
    { to: "/at-home", label: t.nav.atHome, id: "nav-at-home" },
    { to: "/family-meals", label: t.nav.familyMeals, id: "nav-family-meals" },
    { to: "/heart-of-house", label: t.nav.heartOfHouse, id: "nav-heart-of-house" },
    { to: "/about", label: t.nav.about, id: "nav-about" },
    { to: "/media", label: t.nav.media, id: "nav-media" },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      className="sticky top-0 z-50 border-b border-[#242424] bg-[#111111]/85 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mise-container lg:!px-8 xl:!px-20 flex h-16 items-center justify-between gap-4 xl:gap-6">
        <Link
          to="/"
          className="flex items-center"
          data-testid="brand-logo-link"
          onClick={() => setOpen(false)}
        >
          <MiseLogo className="h-8 md:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.id}
              data-track={`nav-${item.id}`}
              className={({ isActive }) =>
                `whitespace-nowrap lg:text-[13px] xl:text-sm ${linkBase} ${isActive ? linkActive : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageToggle lang={lang} setLang={setLang} />
          <Link
            to={pilotCta.to}
            className={`${hidePilotChip ? "hidden" : "hidden md:inline-flex"} mise-pill-primary whitespace-nowrap text-sm !py-2 !px-4`}
            data-testid="header-pilot-cta"
            data-track="header-cta"
          >
            {pilotCta.label}
          </Link>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-[#444444] text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#242424] bg-[#111111]" data-testid="mobile-menu">
          <div className="mise-container py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`mobile-${item.id}`}
                className={({ isActive }) =>
                  `py-2 text-base ${linkBase} ${isActive ? linkActive : ""}`
                }
                onClick={() => setOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            {!hidePilotChip && (
              <Link
                to={pilotCta.to}
                className="mise-pill-primary mt-2 self-start text-sm !py-2 !px-4"
                data-testid="mobile-pilot-cta"
                data-track="mobile-cta"
                onClick={() => setOpen(false)}
              >
                {pilotCta.label}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

const LanguageToggle = ({ lang, setLang }) => (
  <div
    className="inline-flex items-center rounded-full border border-[#444444] p-0.5 text-xs"
    role="group"
    aria-label="Language toggle"
    data-testid="language-toggle"
  >
    {["en", "fr"].map((code) => {
      const active = lang === code;
      return (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          data-track={`lang-${code}`}
          className={`px-3 py-1 rounded-full transition-colors ${
            active ? "bg-[#E8490F] text-white" : "text-white/70 hover:text-white"
          }`}
        >
          {code.toUpperCase()}
        </button>
      );
    })}
  </div>
);

export default Header;
